/**
 * This file contains "join" selectors. These selectors create view-models by combining
 * two (or more) selectors across different feature stores.
 *
 * avoid using barrel imports because they can lead to circular dependencies
 */
import { createSelector } from '@ngrx/store';
import * as fromSeriousSystem from '@serious-system';
import { GENERIC_ASSISTANT_CONFIGURATION_FILENAME } from '@shared';
import type * as fromGenerated from '../../../_generated';
import { hasRemovePromptInput } from '../../assistants/assistant-builder/assistant-builder.helper';
import { assistantsFeature } from '../../assistants/store/assistants.reducer';
import { chatsFeature } from '../../store';

/**
 * Selector that determines the assistant associated with the currently selected chat.
 *
 * This selector returns the assistant entity corresponding to the selected chat's assistant ID.
 * The assistant is determined directly from the chat's assistantId property.
 *
 * @param {function} chatsFeature.selectSelectedChat - Selector to retrieve the currently selected chat.
 * @param {function} assistantsFeature.selectEntities - Selector to retrieve the entities (assistants) mapped by their IDs.
 * @returns {fromGenerated.AssistantView|undefined} The assistant entity associated with the selected chat.
 * Returns undefined if the chat has no assistantId or if the assistant entity is not found.
 */
export const selectSelectedChatAssistant = createSelector(
  chatsFeature.selectSelectedChat,
  assistantsFeature.selectEntities,
  (selectedChat, assistantEntities) => {
    return assistantEntities[selectedChat?.assistantId ?? -1];
  },
);

/**
 * Selector that returns all chats as item navigation associated with the selected assistant.
 *
 * @param {function} chatsFeature.selectChatsAsItemNavigation - Selector to retrieve all chats as item navigation.
 * @param {function} selectSelectedChatAssistant - Selector to retrieve the selected assistant.
 * @returns {ChatNavigationItem[]} The chats as item navigation associated with the selected assistant.
 */
export const selectChatNavigationItemsBySelectedAssistant = createSelector(
  chatsFeature.selectChatNavigationItems,
  selectSelectedChatAssistant,
  (chatNavigationItems, selectedChatAssistant) =>
    chatNavigationItems.filter(
      (chatNavigationItem) =>
        chatNavigationItem.assistantId === selectedChatAssistant?.uuid,
    ),
);

/**
 * Whether a document is the skill's skill.md configuration file (SB-3303/T9,
 * display half of KD-15): `instruction`-role docs never render as knowledge
 * sources. The filename fallback covers legacy generic rows that keep role
 * `regular` until PR-2's backfill normalizes them, and is scoped to
 * ASSISTANT-owned docs only — a user-uploaded chat/message doc named skill.md
 * is genuine content and keeps rendering (QA#2).
 */
const isInstructionFile = (
  document: Pick<fromGenerated.DocumentView, 'role' | 'filename'>,
  isAssistantDocument: boolean,
): boolean =>
  document.role === 'instruction' ||
  (isAssistantDocument &&
    document.filename.trim().toLowerCase() ===
      GENERIC_ASSISTANT_CONFIGURATION_FILENAME);

const documentsToSource = (
  document: Pick<
    fromGenerated.DocumentView,
    'uuid' | 'filename' | 'extension' | 'status' | 'deletedAt'
  >,
  chatMessageId?: number,
) => {
  const fileType = fromSeriousSystem.getFileTypeIconFromExtension(
    document.extension,
  );
  return {
    uuid: document.uuid,
    filename: document.filename,
    fileType,
    /**
     * Semantic flag so the Sources panel can route an image source to the
     * overlay viewer instead of the PDF-only document viewer.
     * Derived from the extension→icon mapping.
     */
    isImage: fileType === fromSeriousSystem.FileTypeIconsEnum.image,
    status: document.status,
    chatMessageId: chatMessageId ?? null,
  };
};

/**
 * Selector that returns all documents associated with the selected chat grouped into:
 * - fromChat: Documents attached to chat but not yet in messages
 * - fromUserChatMessages: Documents attached to the user's chat messages
 * - fromChatAssistant: Documents belonging to the chat's assistant
 * - regularDocuments: Documents attached to the user's chat messages with role 'regular'
 * - referenceDocuments: Documents attached to the user's chat messages with role 'reference'
 */
export const selectSelectedChatDocuments = createSelector(
  chatsFeature.selectSelectedChat,
  selectSelectedChatAssistant,
  (selectedChat, selectedChatAssistant) => {
    const fromUserChatMessages: (fromGenerated.DocumentView & {
      chatMessageId: number;
    })[] =
      selectedChat?.messages
        ?.filter((message) => message.isFromUser)
        ?.flatMap((message) =>
          (message.documents ?? []).map((document) => ({
            ...document,
            chatMessageId: message.id,
          })),
        ) ?? [];

    /**
     * Avoids issues with the transient state where a document would be attached
     * to both the chat and the chat messages before being removed from the chat.
     */
    const fromChat = (selectedChat?.documents ?? []).filter(
      (fromChatDoc) =>
        !fromUserChatMessages.some(
          (fromUserChatMsgDoc) => fromUserChatMsgDoc.uuid === fromChatDoc.uuid,
        ),
    );

    const fromChatAssistant = selectedChatAssistant?.documents ?? [];
    const lastChatbotMessageOutputDocuments =
      selectedChat?.messages
        ?.filter((message) => !message.isFromUser)
        ?.at(-1)
        ?.documents?.filter((document) => document.direction === 'output') ??
      [];

    return {
      fromChat,
      fromUserChatMessages,
      fromChatAssistant,
      lastChatbotMessageOutputDocuments,
      toSources: {
        // T9 (SB-3303): the skill.md never renders as a knowledge source —
        // filtered here, inside toSources only. The raw fromChat /
        // fromUserChatMessages / fromChatAssistant arrays above stay
        // unfiltered (seven selectors in conversation-prompt.selectors
        // depend on them for upload gating, counts and phases).
        default: [
          ...fromChatAssistant.filter((doc) => !isInstructionFile(doc, true)),
          ...fromUserChatMessages.filter(
            (doc) => !isInstructionFile(doc, false),
          ),
          /**
           * If the trait config has removePromptInput set to true,
           * include the fromChat documents in the default sources so
           * the user can see them in the assistant profile.
           */
          ...(hasRemovePromptInput(selectedChatAssistant?.traitConfig)
            ? fromChat.filter((doc) => !isInstructionFile(doc, false))
            : []),
        ]
          .filter((doc) => !doc.deletedAt)
          .sort((a, b) => a.filename.localeCompare(b.filename))
          .map((doc) =>
            documentsToSource(
              doc,
              'chatMessageId' in doc
                ? (doc.chatMessageId as number)
                : undefined,
            ),
          ),
        forComplianceTrait: {
          references: [
            ...fromChat.filter((doc) => doc.role === 'reference'),
            ...fromChatAssistant.filter((doc) => doc.role === 'reference'),
            ...fromUserChatMessages.filter((doc) => doc.role === 'reference'),
          ]
            .filter((doc) => !doc.deletedAt)
            .sort((a, b) => a.filename.localeCompare(b.filename))
            .map((doc) =>
              documentsToSource(
                doc,
                'chatMessageId' in doc
                  ? (doc.chatMessageId as number)
                  : undefined,
              ),
            ),
          regulars: [
            ...fromChat.filter((doc) => doc.role === 'regular'),
            ...fromUserChatMessages.filter((doc) => doc.role === 'regular'),
          ]
            .filter((doc) => !doc.deletedAt)
            .sort((a, b) => a.filename.localeCompare(b.filename))
            .map((doc) =>
              documentsToSource(
                doc,
                'chatMessageId' in doc ? doc.chatMessageId : undefined,
              ),
            ),
        },
      },
    };
  },
);

/**
 * A selector used to determine if the current conversation is new without an associated assistant.
 *
 * This function evaluates two criteria to make its determination:
 * 1. Checks if the selected chat contains no messages (message count is 0).
 * 2. Checks if the chat has no assistantId.
 *
 * Both criteria must be true for this selector to return `true`.
 *
 * Dependencies:
 * - `chatsFeature.selectSelectedChat`: Selector that returns the currently selected chat object.
 * - `selectSelectedChatAssistant`: Selector that returns the currently selected chat assistant object.
 *
 * @constant {Function} selectIsNewConversationWithoutAssistant - The memoized selector function.
 * @returns {boolean} - `true` if the selected chat contains no messages and has no assistantId, otherwise `false`.
 */
export const selectIsNewConversationWithoutAssistant = createSelector(
  chatsFeature.selectSelectedChat,
  selectSelectedChatAssistant,
  (selectedChat, selectedChatAssistant) =>
    (selectedChat?._count?.messages ?? 0) === 0 && !selectedChatAssistant,
);

export const selectIsNewConversationWithAssistant = createSelector(
  chatsFeature.selectSelectedChat,
  selectSelectedChatAssistant,
  (selectedChat, selectedChatAssistant) =>
    (selectedChat?._count?.messages ?? 0) === 0 && selectedChatAssistant
      ? true
      : false,
);

/**
 * A selector function that computes the view model for the selected document.
 * It determines the document and its related data based on the selected document ID,
 * the chat's assistant ID, and related chat message information.
 *
 * We don't have a feature store for documents. The document details are found at different places
 * depending on where the document was accessed from.
 * - If the document is attached to a chatbot's answer, then the document details are in the chunks
 * - If the document is attached to a user's message, then the document details are in the documents
 *   attached to that message
 * - If the document is not attached to a chat message (and only to a chat), then the document details
 *   are in the assistant attached to that chat
 *
 * The processing follows these rules:
 * 1. If no document ID is selected, the result is `null`.
 * 2. If the selected document is associated with a chat message,
 *    the view model is based on that document directly.
 * 3. If the document is not associated with a chat message, it combines data from
 *    the chat's assistant and document information.
 *
 * @param {Function} chatsFeature.selectSelectedDocumentFromChatMessage - Selector for the document associated with a chat message.
 * @param {Function} chatsFeature.selectSelectedDocumentId - Selector for the currently selected document ID.
 * @param {Function} selectSelectedChatAssistant - Selector for the assistant associated with the selected chat.
 * @param {Function} assistantsFeature.selectEntities - Selector for assistant entities.
 * @returns {Object|null} The view model for the selected document, or `null` if no document or assistant data is available.
 */
export const selectSelectedDocumentViewModel = createSelector(
  chatsFeature.selectSelectedDocumentFromChatMessage,
  chatsFeature.selectSelectedDocumentId,
  selectSelectedChatAssistant,
  assistantsFeature.selectEntities,
  (
    selectedDocumentFromChatMessage,
    selectedDocumentId,
    selectedChatAssistant,
    assistantEntities,
  ) => {
    if (!selectedDocumentId) {
      return null;
    }

    // Document is associated to a chat message
    if (selectedDocumentFromChatMessage) {
      return selectedDocumentFromChatMessage;
    }

    // Document is not associated to a chat message
    const assistantId = selectedChatAssistant?.uuid;
    if (!assistantId) {
      return null;
    }

    const assistant = assistantEntities[assistantId];
    if (!assistant) {
      return null;
    }

    return {
      document: assistant.documents.find(
        (document) => document.uuid === selectedDocumentId,
      ),
      documentChunks: [],
      documentCitations: [],
    };
  },
);
